/**
 * Loads the media a canvas node references, as bytes or as base64, for provider
 * API calls and for browser-side processing such as clip trimming.
 *
 * Nodes hold media in several shapes: data URLs, `blob:` URLs (fresh uploads and
 * files read from the local auto-save folder), `file://` registry references
 * (Firebase Storage), plain https URLs and, in old workspaces, retired `mongo://`
 * references. Each generator node used to carry its own copy of this logic, and
 * every copy ended by `fetch`ing whatever string it had left, including `file://`
 * references, which the browser refuses ("Not allowed to load local resource"),
 * so the real failure was hidden behind a generic "Failed to load image".
 *
 * `file://` references resolve in this order: the local auto-save folder through the
 * in-memory reference map, bytes this session still holds from uploading the file,
 * the local auto-save folder by the storage path this device remembers for the file,
 * the registry (the user's own, then the workspace owner's for shared workspaces; read
 * once per session), the local auto-save folder by the registry's storage path, then
 * cloud storage. In the desktop app a folder file's bytes come from the app's own server.
 * Folder reads use the signed-in user's folder and never ask for access. Cloud storage is read through
 * the stored download URL first: a plain fetch that the media cache can answer and
 * that is unaffected by whatever aborts the SDK's XMLHttpRequests. The SDK is the
 * fallback and is called once, because it already retries network failures and
 * aborted requests internally for up to two minutes. Every failure is a
 * `MediaLoadError` whose message names the file and says what to do about it. A copy downloaded
 * from the cloud is kept in the local auto-save folder when one is connected (keepCopyInFolder),
 * so a file is downloaded once; callers that keep copies themselves say `keepInFolder: false`.
 */

import { getStorage, ref as storageRef, getBlob, getDownloadURL } from 'firebase/storage';
import { getCurrentIdToken, getFirebaseApp, isOffline } from './storage';
import { getFileMeta, knownStoragePath, extractFileId, isFileReference, sessionBlobForRef, type FileMetadata } from './fileRegistry';
import { autoSaveFile, localPathForRef, getLocalFolderAccess, readLocalFile, parseStoragePath, registerLocalFileMapping, storageWorkspaceOf, type SaveCategory } from './localSave';
import { isLiveLocalMediaUrl, isLocalMediaUrl } from './desktopFolder';
import { isLegacyMediaRef } from './mediaStorage';

export interface LoadedMedia {
    /** Base64 payload without the `data:` prefix. */
    data: string;
    mimeType: string;
}

export interface LoadedMediaBlob {
    blob: Blob;
    mimeType: string;
}

export interface LoadMediaOptions {
    userId?: string;
    /** Owner of a shared workspace; their registry is tried when the file is not in the user's own. */
    ownerUserId?: string;
    /** Workspace name, used to look in the local auto-save folder first. */
    workspaceName?: string;
    /**
     * What to call the media in error messages. It appears mid-sentence, so use a
     * file name or a phrase such as "the first frame" or "Reference 2".
     */
    label?: string;
    /** Used when neither the response nor the registry says what the bytes are. */
    defaultMimeType?: string;
    /**
     * Whether the bytes may be downloaded. With false, only copies already on this device
     * are used (memory and the local auto-save folder; the registry is asked for the file's
     * name only while the folder can be read), and a MediaLoadError with code 'not-local' is
     * thrown when there is none. Defaults to true.
     */
    network?: boolean;
    /** Bytes received so far while the file is downloaded (total is null when unknown). */
    onDownloadProgress?: (receivedBytes: number, totalBytes: number | null) => void;
    /**
     * Whether a copy downloaded from the cloud is kept in the local auto-save folder, under the
     * workspace's place with the cloud copy's category and name, when a folder is connected and
     * may be written without asking. Defaults to true; needs `workspaceName` and `userId`.
     */
    keepInFolder?: boolean;
}

/** Where loaded bytes came from: this session's memory, the local auto-save folder, or a download. */
export type MediaSource = 'memory' | 'folder' | 'network';

export interface LoadedMediaWithSource extends LoadedMediaBlob {
    source: MediaSource;
}

export class MediaLoadError extends Error {
    readonly ref: string;
    readonly code?: string;

    constructor(message: string, ref: string, code?: string) {
        super(message);
        this.name = 'MediaLoadError';
        this.ref = ref;
        this.code = code;
    }
}

const MIME_BY_EXTENSION: Record<string, string> = {
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
    mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', ogg: 'audio/ogg',
    mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
};

const errorCode = (e: unknown): string | undefined =>
    typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string'
        ? (e as { code: string }).code
        : undefined;

const mimeFromName = (name: string | undefined): string | undefined => {
    if (!name) return undefined;
    const clean = name.split(/[?#]/)[0];
    const dot = clean.lastIndexOf('.');
    const ext = dot >= 0 ? clean.slice(dot + 1).toLowerCase() : '';
    return ext ? MIME_BY_EXTENSION[ext] : undefined;
};

/** A MIME type worth reporting; the generic binary type says nothing about the bytes. */
const usableType = (type: string | undefined): string | undefined =>
    type && type !== 'application/octet-stream' ? type : undefined;

const readBlobAsBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const comma = result.indexOf(',');
            if (comma < 0) {
                reject(new Error('Could not read file contents'));
                return;
            }
            resolve(result.slice(comma + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error('Could not read file contents'));
        reader.readAsDataURL(blob);
    });

const finish = (blob: Blob, mediaRef: string, label: string, defaultMime: string, hint?: string): LoadedMediaBlob => {
    if (blob.size === 0) throw new MediaLoadError(`There is no data in ${label}. Upload or generate it again.`, mediaRef, 'empty');
    return { blob, mimeType: usableType(blob.type) || usableType(hint) || defaultMime };
};

/** The server answered, but with an error status: the transport works, the URL does not. */
class HttpStatusError extends Error {
    constructor(readonly status: number) {
        super(`HTTP ${status}`);
        this.name = 'HttpStatusError';
    }
}

type DownloadProgress = LoadMediaOptions['onDownloadProgress'];

/** The response's bytes, reporting how many have arrived as they come in. */
const readWithProgress = async (response: Response, onProgress: DownloadProgress): Promise<Blob> => {
    if (!onProgress || !response.body) return response.blob();
    const header = Number(response.headers.get('content-length'));
    const total = Number.isFinite(header) && header > 0 ? header : null;
    const reader = response.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;
    onProgress(0, total);
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value as Uint8Array<ArrayBuffer>);
        received += value.byteLength;
        onProgress(received, total);
    }
    return new Blob(chunks, { type: response.headers.get('content-type') || '' });
};

const fetchBlob = async (url: string, onProgress?: DownloadProgress): Promise<Blob> => {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new HttpStatusError(response.status);
    return readWithProgress(response, onProgress);
};

/** Bound for the fresh download URL request; the SDK would otherwise retry it for two minutes. */
const FRESH_URL_TIMEOUT_MS = 10_000;

const withTimeout = <T,>(promise: Promise<T>, ms: number, what: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`${what} timed out after ${ms} ms`)), ms);
        promise.then(
            value => { clearTimeout(timer); resolve(value); },
            error => { clearTimeout(timer); reject(error); },
        );
    });

const dataUrlMime = (url: string, defaultMime: string): string => {
    const comma = url.indexOf(',');
    const header = comma < 0 ? '' : url.slice('data:'.length, comma);
    return header.split(';')[0] || defaultMime;
};

const fromDataUrl = async (url: string, label: string, defaultMime: string): Promise<LoadedMediaBlob> => {
    if (url.indexOf(',') < 0) throw new MediaLoadError(`The data for ${label} is not valid.`, url.slice(0, 32), 'malformed');
    const blob = await (await fetch(url)).blob();
    return finish(blob, url.slice(0, 32), label, defaultMime, dataUrlMime(url, defaultMime));
};

const fromBlobUrl = async (url: string, label: string, defaultMime: string): Promise<LoadedMediaBlob> => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return finish(await response.blob(), url, label, defaultMime);
    } catch (e) {
        if (e instanceof MediaLoadError) throw e;
        throw new MediaLoadError(
            `The temporary copy of ${label} is no longer available in this session. Upload it again.`,
            url,
            'blob-expired',
        );
    }
};

/**
 * An address the desktop app streams a folder file from (what a view shows a local file by).
 * The bytes are on this device and come through the app's own server. The address holds
 * that run's id, which is kept out of error messages, and one made by an earlier run of the
 * app cannot be read any more.
 */
const fromLocalMediaUrl = async (url: string, label: string, defaultMime: string): Promise<LoadedMediaBlob> => {
    const shown = url.replace(/\/__folder\/[^/]+/, '/__folder/...').split('?')[0];
    try {
        if (!isLiveLocalMediaUrl(url)) throw new Error('The address is from an earlier run of the app');
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return finish(await response.blob(), shown, label, defaultMime, mimeFromName(new URL(url).pathname));
    } catch (e) {
        if (e instanceof MediaLoadError) throw e;
        throw new MediaLoadError(
            `The local copy of ${label} can no longer be read in this session. Open the workspace again, or upload the file again.`,
            shown,
            'local-expired',
        );
    }
};

/**
 * Reads a file from the local auto-save folder of the signed-in user (the folder and the
 * auto-save switch are stored per user), or null when it is not there. A cloud storage
 * path also names the workspace the file was first saved in, which is looked in too
 * (after a rename, or for media reused from another workspace). An empty file (an
 * interrupted save) counts as absent so the cloud copy is used.
 */
const readLocalCopy = async (workspaceName: string, storagePath: string, userId: string | undefined): Promise<Blob | null> => {
    const info = parseStoragePath(storagePath);
    if (!info?.category || !info.filename) return null;
    try {
        const file = await readLocalFile(workspaceName, info.category, info.filename, userId, storageWorkspaceOf(storagePath));
        return file && file.size > 0 ? file : null;
    } catch {
        return null;
    }
};

/** Bytes this session still holds for a file it uploaded, or null when they are gone. */
const readSessionCopy = async (mediaRef: string): Promise<Blob | null> => {
    const blobUrl = sessionBlobForRef(mediaRef);
    if (!blobUrl) return null;
    try {
        const response = await fetch(blobUrl);
        if (!response.ok) return null;
        const blob = await response.blob();
        return blob.size > 0 ? blob : null;
    } catch {
        return null; // released since
    }
};

const notLocal = (mediaRef: string, label: string): MediaLoadError =>
    new MediaLoadError(`${label.charAt(0).toUpperCase()}${label.slice(1)} is not on this device.`, mediaRef, 'not-local');

const describeStorageFailure = (label: string, code: string | undefined): string => {
    switch (code) {
        case 'storage/unauthenticated':
        case 'storage/unauthorized':
            return `You do not have permission to download ${label}. Sign out and back in, then try again; if this is a shared workspace, ask its owner to check that it is still shared with you.`;
        case 'storage/object-not-found':
            return `Cloud storage has no copy of ${label}. Upload or generate it again.`;
        case 'storage/canceled':
            return `The browser cancelled the download of ${label} before it finished. Try again; if it keeps happening, a download-manager or ad-blocking extension may be interfering with this site.`;
        case 'storage/retry-limit-exceeded':
        case 'download-stopped':
            return `Downloading ${label} kept being stopped before it finished. Check your connection; if it works, a download-manager or ad-blocking extension may be stopping downloads from this site, so allow the site in it and try again.`;
        default:
            return `Could not download ${label} from cloud storage${code ? ` (${code})` : ''}. Check your connection and try again.`;
    }
};

/** Files larger than this are downloaded in pieces from the start. */
const LARGE_FILE_BYTES = 8 * 1024 * 1024;
const PIECE_BYTES = 4 * 1024 * 1024;
/** A piece that is stopped on the way is asked for again this many times. */
const PIECE_RETRIES = 2;
/** The SDK retries stopped requests for two minutes; it gets this long. */
const SDK_TIMEOUT_MS = 20_000;

const totalFromContentRange = (header: string | null): number | null => {
    const match = /\/(\d+)\s*$/.exec(header || '');
    return match ? Number(match[1]) : null;
};

/** One range of a file; a request stopped on the way is retried, a server's answer is not. */
const fetchPiece = async (url: string, headers: Record<string, string>, from: number, to: number): Promise<Response> => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= PIECE_RETRIES; attempt++) {
        try {
            const response = await fetch(url, { headers: { ...headers, Range: `bytes=${from}-${to}` }, cache: 'no-store' });
            if (response.status === 206 || response.status === 200) return response;
            throw new HttpStatusError(response.status);
        } catch (e) {
            if (e instanceof HttpStatusError) throw e;
            lastError = e;
        }
    }
    throw lastError;
};

/**
 * Downloads a file in pieces of a few MB, the way video players ask for video. Some
 * download-manager extensions stop one long download of a video file but leave these
 * partial requests alone, and a piece that fails is asked for again on its own. A server
 * that ignores ranges sends the whole file at once, which is fine too.
 */
const fetchInPieces = async (url: string, headers: Record<string, string>, onProgress?: DownloadProgress): Promise<Blob> => {
    const first = await fetchPiece(url, headers, 0, PIECE_BYTES - 1);
    if (first.status === 200) return readWithProgress(first, onProgress);
    const total = totalFromContentRange(first.headers.get('content-range'));
    const type = first.headers.get('content-type') || '';
    const parts: Blob[] = [await first.blob()];
    let received = parts[0].size;
    onProgress?.(received, total);
    while (total === null ? parts[parts.length - 1].size === PIECE_BYTES : received < total) {
        const to = total === null ? received + PIECE_BYTES - 1 : Math.min(total, received + PIECE_BYTES) - 1;
        let piece: Response;
        try {
            piece = await fetchPiece(url, headers, received, to);
        } catch (e) {
            if (e instanceof HttpStatusError && e.status === 416) break; // past the end of a file of unknown size
            throw e;
        }
        if (piece.status !== 206) throw new Error('The server stopped sending the file in parts.');
        const blob = await piece.blob();
        if (blob.size === 0) break;
        parts.push(blob);
        received += blob.size;
        onProgress?.(received, total);
    }
    return new Blob(parts, { type });
};

/**
 * Downloads a Storage object, without depending on the SDK, whose requests some browser
 * extensions stop: the stored download URL (a large file, or one whose whole download was
 * stopped, in pieces); then, signed in, the same download with the user's token; then one
 * SDK attempt, bounded; then a fresh download URL when a server answered that the stored
 * one's token was refused.
 */
const downloadFromStorage = async (
    storagePath: string,
    knownDownloadUrl: string | undefined,
    mediaRef: string,
    label: string,
    onProgress?: DownloadProgress,
    sizeHint?: number,
): Promise<Blob> => {
    // Whether a server answered with an error (a refused token, a missing file), as opposed
    // to every request being stopped on the way
    let answered = false;
    const attempt = async (what: string, run: () => Promise<Blob>): Promise<Blob | null> => {
        try {
            return await run();
        } catch (e) {
            if (e instanceof HttpStatusError) answered = true;
            console.warn(`[mediaLoader] ${what} failed for ${storagePath}:`, e);
            return null;
        }
    };
    if (knownDownloadUrl) {
        const large = (sizeHint ?? 0) > LARGE_FILE_BYTES;
        const blob = await attempt('stored download URL', () => (large ? fetchInPieces(knownDownloadUrl, {}, onProgress) : fetchBlob(knownDownloadUrl, onProgress)))
            ?? (!large && !answered ? await attempt('stored download URL in pieces', () => fetchInPieces(knownDownloadUrl, {}, onProgress)) : null);
        if (blob) return blob;
    }
    if (isOffline()) {
        throw new MediaLoadError(`You appear to be offline, so ${label} could not be downloaded. Reconnect and try again.`, mediaRef, 'offline');
    }

    const app = getFirebaseApp();
    if (!app) {
        throw new MediaLoadError(`Cloud storage is not available, so ${label} cannot be loaded. Reload the page and try again.`, mediaRef, 'no-firebase');
    }
    const objectRef = storageRef(getStorage(app), storagePath);

    // Signed in: the file's own address with the user's token, still a plain download
    const bucket = (objectRef as { bucket?: string }).bucket;
    const token = bucket ? await getCurrentIdToken().catch(() => null) : null;
    if (bucket && token) {
        const direct = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(storagePath)}?alt=media`;
        try {
            return await fetchInPieces(direct, { Authorization: `Firebase ${token}` }, onProgress);
        } catch (e) {
            if (e instanceof HttpStatusError) {
                answered = true;
                if (e.status === 404) throw new MediaLoadError(describeStorageFailure(label, 'storage/object-not-found'), mediaRef, 'storage/object-not-found');
                if (e.status === 401 || e.status === 403) throw new MediaLoadError(describeStorageFailure(label, 'storage/unauthorized'), mediaRef, 'storage/unauthorized');
            }
            console.warn(`[mediaLoader] signed-in download failed for ${storagePath}:`, e);
        }
    }

    let code: string | undefined;
    try {
        return await withTimeout(getBlob(objectRef), SDK_TIMEOUT_MS, 'SDK download');
    } catch (e) {
        // A stopped SDK retries for minutes; its time running out means the same as its retries running out
        code = errorCode(e) ?? (answered ? undefined : 'download-stopped');
        console.warn(`[mediaLoader] SDK download failed for ${storagePath}:`, e);
    }

    // A fresh download URL only helps when a server refused the stored one's token (or there was none)
    if (code === 'storage/object-not-found' || code === 'storage/retry-limit-exceeded' || code === 'download-stopped' || (!!knownDownloadUrl && !answered)) {
        throw new MediaLoadError(describeStorageFailure(label, code), mediaRef, code);
    }

    try {
        const freshUrl = await withTimeout(getDownloadURL(objectRef), FRESH_URL_TIMEOUT_MS, 'Fresh download URL');
        if (freshUrl !== knownDownloadUrl) return await fetchInPieces(freshUrl, {}, onProgress);
    } catch (e) {
        console.warn(`[mediaLoader] fresh download URL failed for ${storagePath}:`, e);
    }
    throw new MediaLoadError(describeStorageFailure(label, code), mediaRef, code);
};

/**
 * A downloaded copy into the local auto-save folder, in the background: under the workspace's
 * place, with the cloud copy's category and name (where readLocalCopy looks), and noted in the
 * workspace's map of local files. Only when the folder may be written without asking; a failure
 * is logged and nothing more.
 */
const keepCopyInFolder = (options: LoadMediaOptions, mediaRef: string, storagePath: string, blob: Blob): void => {
    const { workspaceName, userId } = options;
    if (options.keepInFolder === false || !workspaceName || !userId) return;
    const info = parseStoragePath(storagePath);
    if (!info?.category || !info.filename || !/^[A-Za-z0-9_.-]+$/.test(info.category) || !/^[A-Za-z0-9_.-]+$/.test(info.filename)) return;
    void (async () => {
        try {
            const access = await getLocalFolderAccess(userId);
            if (access?.state !== 'granted') return;
            const extension = /\.([A-Za-z0-9]+)$/.exec(info.filename)?.[1] ?? 'bin';
            const saved = await autoSaveFile(workspaceName, info.category as SaveCategory, blob, extension, info.filename, userId);
            if (saved.success) registerLocalFileMapping(mediaRef, `${info.category}/${info.filename}`);
        } catch (e) {
            console.warn(`[mediaLoader] the downloaded copy of ${info.filename} was not kept in the folder:`, e);
        }
    })();
};

const fromFileReference = async (mediaRef: string, options: LoadMediaOptions, label: string, defaultMime: string): Promise<LoadedMediaWithSource> => {
    const fileId = extractFileId(mediaRef);
    if (!fileId) throw new MediaLoadError(`The file reference for ${label} is invalid.`, mediaRef, 'malformed');
    const { userId, ownerUserId, workspaceName } = options;

    // 1. Local auto-save folder through the reference map (works offline and signed out).
    if (workspaceName) {
        const localPath = localPathForRef(mediaRef);
        if (localPath) {
            const local = await readLocalCopy(workspaceName, localPath, userId);
            if (local) return { ...finish(local, mediaRef, label, defaultMime, mimeFromName(localPath)), source: 'folder' };
        }
    }

    // 2. Bytes still in memory from an upload this session (a video generated a moment ago),
    //    for when the folder cannot be read. After the folder, so a caller that keeps copies
    //    does not copy what is already on disk.
    const session = await readSessionCopy(mediaRef);
    if (session) return { ...finish(session, mediaRef, label, defaultMime), source: 'memory' };

    if (!userId) {
        throw new MediaLoadError(`Sign in to use ${label}: it is stored in your cloud library.`, mediaRef, 'unauthenticated');
    }

    // 3. Local auto-save folder by the storage path this device remembers for the file: its
    //    name in the folder, so a file seen here before is found without asking the registry.
    const remembered = knownStoragePath(userId, fileId);
    if (workspaceName && remembered) {
        const local = await readLocalCopy(workspaceName, remembered, userId);
        if (local) return { ...finish(local, mediaRef, label, defaultMime, mimeFromName(remembered)), source: 'folder' };
    }

    // Without downloads, the registry is only worth asking when its answer can lead to a readable local copy
    if (options.network === false) {
        const folderReadable = !!workspaceName && (await getLocalFolderAccess(userId).catch(() => null))?.state === 'granted';
        if (!folderReadable) throw notLocal(mediaRef, label);
    }

    // 4. Registry lookup, once per session: the user's own, then the workspace owner's for shared workspaces.
    let meta: FileMetadata | null;
    try {
        meta = await getFileMeta(userId, fileId);
        if (!meta && ownerUserId && ownerUserId !== userId) meta = await getFileMeta(ownerUserId, fileId);
    } catch (e) {
        const code = errorCode(e);
        throw new MediaLoadError(
            `Could not look up ${label} in your library${code ? ` (${code})` : ''}. Check your connection and try again.`,
            mediaRef,
            code,
        );
    }
    if (!meta?.storagePath) {
        throw new MediaLoadError(`Your library has no entry for ${label}. It may have been deleted; upload or generate it again.`, mediaRef, 'not-found');
    }
    const hint = usableType(meta.contentType) || mimeFromName(meta.storagePath);

    // 5. Local auto-save folder by the registry's storage path (the same copy the canvas displays).
    if (workspaceName && meta.storagePath !== remembered) {
        const local = await readLocalCopy(workspaceName, meta.storagePath, userId);
        if (local) return { ...finish(local, mediaRef, label, defaultMime, hint), source: 'folder' };
    }

    // 6. Cloud.
    if (options.network === false) throw notLocal(mediaRef, label);
    const blob = await downloadFromStorage(meta.storagePath, meta.downloadUrl || undefined, mediaRef, label, options.onDownloadProgress, meta.size);
    keepCopyInFolder(options, mediaRef, meta.storagePath, blob);
    return { ...finish(blob, mediaRef, label, defaultMime, hint), source: 'network' };
};

/** Object path inside the default bucket for a Firebase Storage download URL, or null for any other URL. */
export const storagePathFromDownloadUrl = (url: string): string | null => {
    try {
        const parsed = new URL(url);
        if (!/(^|\.)firebasestorage\.googleapis\.com$/i.test(parsed.hostname)) return null;
        const match = parsed.pathname.match(/\/o\/(.+)$/);
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
};

const fromHttpUrl = async (url: string, options: LoadMediaOptions, label: string, defaultMime: string): Promise<LoadedMediaWithSource> => {
    const storagePath = storagePathFromDownloadUrl(url);
    if (storagePath) {
        // Older nodes hold the download URL itself; the auto-save folder may still have the file.
        if (options.workspaceName) {
            const local = await readLocalCopy(options.workspaceName, storagePath, options.userId);
            if (local) return { ...finish(local, url, label, defaultMime, mimeFromName(storagePath)), source: 'folder' };
        }
        if (options.network === false) throw notLocal(url, label);
        const blob = await downloadFromStorage(storagePath, url, url, label, options.onDownloadProgress);
        keepCopyInFolder(options, url, storagePath, blob);
        return { ...finish(blob, url, label, defaultMime, mimeFromName(storagePath)), source: 'network' };
    }
    if (options.network === false) throw notLocal(url, label);

    let host = url;
    try {
        const target = new URL(url);
        host = target.hostname;
        // A fresh query string sidesteps cached copies that were fetched without CORS headers.
        target.searchParams.set('t', Date.now().toString());
        const response = await fetch(target.toString(), { mode: 'cors', cache: 'no-store' });
        if (!response.ok) {
            throw new MediaLoadError(
                `Could not load ${label} from ${host} (HTTP ${response.status}). The link may have expired or been removed; generate or upload the file again.`,
                url,
                'http-error',
            );
        }
        return { ...finish(await readWithProgress(response, options.onDownloadProgress), url, label, defaultMime, mimeFromName(target.pathname)), source: 'network' };
    } catch (e) {
        if (e instanceof MediaLoadError) throw e;
        throw new MediaLoadError(
            `Could not load ${label} from ${host}. The site may not allow downloads from other origins; upload the file directly instead.`,
            url,
            'fetch-failed',
        );
    }
};

const assertLoadable = (mediaRef: string, label: string): void => {
    if (typeof mediaRef !== 'string' || mediaRef.length === 0) {
        throw new MediaLoadError(`There is nothing to load for ${label}.`, String(mediaRef), 'empty');
    }
    if (isLegacyMediaRef(mediaRef)) {
        throw new MediaLoadError(
            `The copy of ${label} lived in the retired media backend and is no longer available. Upload or generate it again.`,
            mediaRef,
            'legacy',
        );
    }
};

/**
 * Resolves any media reference a node can hold to its bytes plus a MIME type, and says
 * where the bytes came from. Copies on this device come first: this session's memory,
 * then the local auto-save folder; the network is last, and is skipped entirely with
 * `network: false`. Throws a `MediaLoadError` with a user-facing message; never fetches
 * a `file://` string.
 */
export const loadMediaWithSource = async (mediaRef: string, options: LoadMediaOptions = {}): Promise<LoadedMediaWithSource> => {
    const label = options.label || 'this file';
    const defaultMime = options.defaultMimeType || 'application/octet-stream';
    assertLoadable(mediaRef, label);
    if (mediaRef.startsWith('data:')) return { ...(await fromDataUrl(mediaRef, label, defaultMime)), source: 'memory' };
    if (mediaRef.startsWith('blob:')) return { ...(await fromBlobUrl(mediaRef, label, defaultMime)), source: 'memory' };
    if (isFileReference(mediaRef)) return fromFileReference(mediaRef, options, label, defaultMime);
    // A local file as a view shows it in the desktop app: already on this device, so allowed without the network too
    if (isLocalMediaUrl(mediaRef)) return { ...(await fromLocalMediaUrl(mediaRef, label, defaultMime)), source: 'folder' };
    if (/^https?:\/\//i.test(mediaRef)) return fromHttpUrl(mediaRef, options, label, defaultMime);
    throw new MediaLoadError(`The address of ${label} cannot be loaded by this app (${mediaRef.slice(0, 24)}).`, mediaRef, 'unsupported');
};

/** The same, without saying where the bytes came from. */
export const loadMediaBlob = async (mediaRef: string, options: LoadMediaOptions = {}): Promise<LoadedMediaBlob> => {
    const { blob, mimeType } = await loadMediaWithSource(mediaRef, options);
    return { blob, mimeType };
};

/** The same media as base64; a base64 data URL is split without any fetch. */
export const loadMediaAsBase64 = async (mediaRef: string, options: LoadMediaOptions = {}): Promise<LoadedMedia> => {
    const label = options.label || 'this file';
    const defaultMime = options.defaultMimeType || 'application/octet-stream';
    assertLoadable(mediaRef, label);
    if (mediaRef.startsWith('data:')) {
        const comma = mediaRef.indexOf(',');
        if (comma < 0) throw new MediaLoadError(`The data for ${label} is not valid.`, mediaRef.slice(0, 32), 'malformed');
        if (/;base64/i.test(mediaRef.slice('data:'.length, comma))) {
            return { data: mediaRef.slice(comma + 1), mimeType: dataUrlMime(mediaRef, defaultMime) };
        }
    }
    const { blob, mimeType } = await loadMediaBlob(mediaRef, options);
    return { data: await readBlobAsBase64(blob), mimeType };
};

/** The same, as a `data:` URL for providers that take one. */
export const loadMediaAsDataUrl = async (mediaRef: string, options?: LoadMediaOptions): Promise<string> => {
    const { data, mimeType } = await loadMediaAsBase64(mediaRef, options);
    return `data:${mimeType};base64,${data}`;
};
